import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { getNowTimeString, getUtcOffset } from 'utils/datetime'
import { datadogLogs } from '@datadog/browser-logs'
import { useContext } from 'react'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { useQuestionnaireParams } from './useQuestionnaireParams'
import { usePostCallbackWithRetry } from './usePostCallback'

interface RenderedCategoryPosition {
  highlighter_category_id: string
  rendered_highlighter_category_position: number
}

interface TextHighlighterSelection {
  selection_number: number
  highlighter_category_ids: string[]
  text_selection: string
  text_entry?: string
}

interface SendTextHighlighterResponsePayload {
  survey_id: string
  supplier_respondent_id: string
  panel_supplier_code: string
  rendered_entry_position: number
  question_id: string
  rendered_category_positions: RenderedCategoryPosition[]
  selections: TextHighlighterSelection[]
  entering_datetime: string
  submission_datetime: string
  time_zone: string
  utc_offset: string
  is_preview: boolean
}

const useSendTextHighlighterResponse = () => {
  const post = usePostCallbackWithRetry<SendTextHighlighterResponsePayload>()
  const { surveyId, respondentId, panelSupplier, preview } =
    useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress],
    addResponseChoicePromise
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendTextHighlighterResponse = async ({
    renderedEntryPosition,
    questionId,
    renderedCategoryPositions,
    selections,
    enteringDatetime
  }: {
    renderedEntryPosition: number
    questionId: string
    renderedCategoryPositions: RenderedCategoryPosition[]
    selections: TextHighlighterSelection[]
    enteringDatetime: string
  }) => {
    const payload: SendTextHighlighterResponsePayload = {
      survey_id: surveyId,
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier,
      rendered_entry_position: renderedEntryPosition,
      question_id: questionId,
      rendered_category_positions: renderedCategoryPositions,
      selections,
      entering_datetime: enteringDatetime,
      submission_datetime: getNowTimeString(),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset(),
      is_preview: !!preview
    }
    const url = coreRespondentUrl(
      `/surveys/${surveyId}/responses/text_highlighter`
    )

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Text highlighter response was sent', {
          payload,
          url,
          requestDurationMs: Math.round(requestDuration)
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendTextHighlighterResponse'
          )
        }
        datadogLogs.logger.error(
          'Network Failure: Text highlighter response failed to be sent',
          {
            err,
            payload,
            url,
            requestDurationMs: Math.round(requestDuration)
          }
        )
      })

    // Add the promise to the tracker
    addResponseChoicePromise(promise)
  }

  return { sendTextHighlighterResponse }
}

export default useSendTextHighlighterResponse
